import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getAlertLogs,
  getAlerts,
  getAnalyticsSummary,
  getCurrentUser,
  getAssetUrl,
  getDashboards,
  getDevices,
  getMyTenants,
  getMqttConnections,
  getPublicPlans,
  getSites,
  getTenantSettings,
  getTenantPlan,
  getTenantTrialStatus,
  getWebSocketUrl,
  requestPlanUpgrade,
  resendVerification,
  switchTenant,
} from '../services/api';
import {
  Alert,
  AlertLog,
  DashboardSummary,
  Device,
  MyTenantsResponse,
  PlanPublic,
  Site,
  TenantPlan,
  TenantTrialStatus,
  User,
} from '../types';
import AlertToast from '../components/AlertToast';
import HelpDrawer from '../components/HelpDrawer';
import LiveReadings from '../components/LiveReadings';
import AlertsPage from './Alerts';
import AuditPage from './Audit';
import DataExportPage from './DataExport';
import DevicesPage from './Devices';
import DashboardsPanel from './DashboardsPanel';
import MapPage from './Map';
import MqttConnectionsPage from './MqttConnections';
import OnboardingWizard from './OnboardingWizard';
import ReportsPage from './Reports';
import SettingsPage from './Settings';
import SitesPage from './Sites';
import SupportPage from './Support';
import TeamPage from './Team';

interface DashboardProps {
  user: User;
  onLogout: () => void;
  onEndImpersonation?: () => void;
}

type TabId =
  | 'overview'
  | 'dashboards'
  | 'live'
  | 'map'
  | 'alerts'
  | 'devices'
  | 'sites'
  | 'mqtt'
  | 'onboarding'
  | 'export'
  | 'reports'
  | 'team'
  | 'audit'
  | 'support'
  | 'settings';

type NavIconKey =
  | 'gauge'
  | 'dashboard'
  | 'wave'
  | 'map'
  | 'bell'
  | 'chip'
  | 'hierarchy'
  | 'checklist'
  | 'antenna'
  | 'report'
  | 'download'
  | 'shield'
  | 'users'
  | 'help'
  | 'headset'
  | 'gear';

interface NavItem {
  id: TabId;
  label: string;
  icon: NavIconKey;
  badge?: number;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

type TenantBranding = {
  logo_url?: string | null;
  platform_name?: string | null;
  platform_short_name?: string | null;
  platform_description?: string | null;
  branding_enabled?: boolean | null;
};

const tabFeatureMap: Record<TabId, string> = {
  overview: 'overview',
  dashboards: 'dashboards',
  live: 'overview',
  map: 'map',
  alerts: 'alerts',
  devices: 'devices',
  sites: 'sites',
  mqtt: 'mqtt',
  onboarding: 'onboarding',
  export: 'export',
  reports: 'reports',
  team: 'team',
  audit: 'audit',
  support: 'settings',
  settings: 'settings',
};

const pageCopy: Record<TabId, { eyebrow: string; title: string; subtitle: string }> = {
  overview: {
    eyebrow: 'OPERATIONS',
    title: 'Fleet Overview',
    subtitle: 'Live telemetry, asset health, site coverage, and recent alert activity.',
  },
  dashboards: {
    eyebrow: 'DASHBOARD STUDIO',
    title: 'Dashboard Builder',
    subtitle: 'Build tenant dashboards from templates, metric widgets, GPS maps, gauges, and live device states.',
  },
  live: {
    eyebrow: 'MONITOR',
    title: 'Live Sensors',
    subtitle: 'WebSocket sensor readouts with polling fallback for active devices.',
  },
  map: {
    eyebrow: 'MONITOR',
    title: 'Floor Plan',
    subtitle: 'Uploaded SVG layouts with draggable device positions and live state.',
  },
  alerts: {
    eyebrow: 'ALERTS',
    title: 'Alert Management',
    subtitle: 'Threshold rules, notification tests, and triggered alert history.',
  },
  devices: {
    eyebrow: 'MANAGE',
    title: 'Device Fleet',
    subtitle: 'Registered assets, topics, parser mappings, and heartbeat status.',
  },
  sites: {
    eyebrow: 'MANAGE',
    title: 'Sites & Areas',
    subtitle: 'Organize devices by industrial site, area, and floor-plan scope.',
  },
  mqtt: {
    eyebrow: 'INTEGRATIONS',
    title: 'MQTT Bridges',
    subtitle: 'External broker connections, topic traffic, and virtual device mapping.',
  },
  onboarding: {
    eyebrow: 'MANAGE',
    title: 'Device Onboarding',
    subtitle: 'Create device credentials and preview telemetry topics.',
  },
  export: {
    eyebrow: 'ANALYZE',
    title: 'Data Export',
    subtitle: 'Filter and download time-series readings as CSV.',
  },
  reports: {
    eyebrow: 'ANALYZE',
    title: 'Reports',
    subtitle: 'Generate PDFs and manage scheduled industrial reporting.',
  },
  team: {
    eyebrow: 'ORGANIZATION',
    title: 'Team Management',
    subtitle: 'Invite operators, engineers, and administrators into the tenant.',
  },
  audit: {
    eyebrow: 'GOVERNANCE',
    title: 'Audit Log',
    subtitle: 'Track user actions, device changes, alert activity, and system events.',
  },
  support: {
    eyebrow: 'CUSTOMER SUCCESS',
    title: 'Support',
    subtitle: 'Open tickets, attach screenshots, and share feedback with Seedronics support.',
  },
  settings: {
    eyebrow: 'ORGANIZATION',
    title: 'Settings',
    subtitle: 'Tenant timezone, heartbeat, branding, security, and platform preferences.',
  },
};

export default function Dashboard({ user, onLogout, onEndImpersonation }: DashboardProps) {
  const isAdmin = user.role === 'admin';
  const canManageReports = ['admin', 'operator', 'engineer'].includes(user.role);
  const activeTabStorageKey = `dashboard.activeTab.${user.id}`;
  const roleAllowedTabIds = useMemo<TabId[]>(
    () => [
      'overview',
      'dashboards',
      'live',
      'map',
      'alerts',
      'devices',
      'sites',
      'mqtt',
      'onboarding',
      'export',
      ...(canManageReports ? (['reports'] as TabId[]) : []),
      ...(isAdmin ? (['team', 'audit'] as TabId[]) : []),
      'support',
      'settings',
    ],
    [canManageReports, isAdmin]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const [devices, setDevices] = useState<Device[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [alertRules, setAlertRules] = useState<Alert[]>([]);
  const [alertLogs, setAlertLogs] = useState<AlertLog[]>([]);
  const [mqttConnections, setMqttConnections] = useState<any[]>([]);
  const [siteFilter, setSiteFilter] = useState('');
  const [summary, setSummary] = useState<any>(null);
  const [hours, setHours] = useState(24);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 860);
  const [wsConnected, setWsConnected] = useState(false);
  const [tenantPlan, setTenantPlan] = useState<TenantPlan | null>(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [trialStatus, setTrialStatus] = useState<TenantTrialStatus | null>(null);
  const [trialLoading, setTrialLoading] = useState(true);
  const [publicPlans, setPublicPlans] = useState<PlanPublic[]>([]);
  const [publicPlansLoading, setPublicPlansLoading] = useState(true);
  const [myTenants, setMyTenants] = useState<MyTenantsResponse | null>(null);
  const [switchingTenant, setSwitchingTenant] = useState(false);
  const [verifiedAt, setVerifiedAt] = useState(user.email_verified_at || null);
  const [verificationNotice, setVerificationNotice] = useState('');
  const [verificationUrl, setVerificationUrl] = useState(() => sessionStorage.getItem(`verification_url.${user.id}`) || '');
  const [resendingVerification, setResendingVerification] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [tenantBranding, setTenantBranding] = useState<TenantBranding>({});

  const planFeatures = useMemo(() => new Set(tenantPlan?.features || []), [tenantPlan?.features]);
  const planAllowedTabIds = useMemo<TabId[]>(() => {
    if (planLoading || !tenantPlan) {
      return roleAllowedTabIds;
    }
    const filtered = roleAllowedTabIds.filter((tab) => tab === 'support' || planFeatures.has(tabFeatureMap[tab]));
    return filtered;
  }, [planFeatures, planLoading, roleAllowedTabIds, tenantPlan]);
  const allowedTabIds = useMemo<TabId[]>(
    () => planAllowedTabIds.filter((tab) => !isMobile || tab !== 'dashboards'),
    [isMobile, planAllowedTabIds]
  );

  function resolveValidTab(tab: string | null): TabId {
    if (tab && allowedTabIds.includes(tab as TabId)) return tab as TabId;
    const saved = localStorage.getItem(activeTabStorageKey);
    if (saved && allowedTabIds.includes(saved as TabId)) return saved as TabId;
    return allowedTabIds[0] || 'overview';
  }

  const [activeTab, setActiveTab] = useState<TabId>(() =>
    resolveValidTab(searchParams.get('tab'))
  );

  useEffect(() => {
    function handleResize() {
      const nextMobile = window.innerWidth <= 860;
      setIsMobile(nextMobile);
      if (!nextMobile) {
        setMobileMenuOpen(false);
      }
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    localStorage.setItem(activeTabStorageKey, activeTab);
    const currentTab = searchParams.get('tab');
    if (currentTab !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [activeTab, activeTabStorageKey, searchParams, setSearchParams]);

  useEffect(() => {
    if (allowedTabIds.length === 0) return;
    if (!allowedTabIds.includes(activeTab)) {
      setActiveTab(allowedTabIds[0]);
    }
  }, [activeTab, allowedTabIds]);

  useEffect(() => {
    let mounted = true;
    setPlanLoading(true);
    setTrialLoading(true);
    setPublicPlansLoading(true);
    Promise.allSettled([
      getTenantPlan(),
      getTenantTrialStatus(),
      getPublicPlans(),
    ])
      .then(([planResult, trialResult, publicPlansResult]) => {
        if (!mounted) return;
        if (planResult.status === 'fulfilled') {
          setTenantPlan(planResult.value);
        } else {
          console.error('Failed to load tenant plan:', planResult.reason?.response?.data?.error || planResult.reason?.message);
        }
        if (trialResult.status === 'fulfilled') {
          setTrialStatus(trialResult.value);
        } else {
          console.error('Failed to load tenant trial status:', trialResult.reason?.response?.data?.error || trialResult.reason?.message);
        }
        if (publicPlansResult.status === 'fulfilled') {
          setPublicPlans(Array.isArray(publicPlansResult.value) ? publicPlansResult.value : []);
        } else {
          console.error('Failed to load public plans:', publicPlansResult.reason?.response?.data?.error || publicPlansResult.reason?.message);
        }
      })
      .finally(() => {
        if (mounted) {
          setPlanLoading(false);
          setTrialLoading(false);
          setPublicPlansLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    getMyTenants()
      .then((memberships) => {
        if (mounted) setMyTenants(memberships);
      })
      .catch((err) => {
        console.error('Failed to load tenant memberships:', err.response?.data?.error || err.message);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setVerifiedAt(user.email_verified_at || null);
    setVerificationUrl(sessionStorage.getItem(`verification_url.${user.id}`) || '');
    setVerificationNotice('');
  }, [user.email_verified_at, user.id]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [hours, siteFilter]);

  useEffect(() => {
    let closed = false;
    let ws: WebSocket | null = null;
    let retry: number | undefined;
    const wsUrl = getWebSocketUrl('/ws');

    function connect() {
      if (closed) return;
      ws = new WebSocket(wsUrl);
      ws.onopen = () => setWsConnected(true);
      ws.onerror = () => setWsConnected(false);
      ws.onclose = () => {
        setWsConnected(false);
        if (!closed) {
          retry = window.setTimeout(connect, 5000);
        }
      };
    }

    connect();
    return () => {
      closed = true;
      if (retry) window.clearTimeout(retry);
      ws?.close();
    };
  }, []);

  async function loadData() {
    const siteId = siteFilter || undefined;
    const [devs, sum, siteList, rules, logs, mqtt] = await Promise.all([
      safeLoad(() => getDevices(), [] as Device[]),
      safeLoad(() => getAnalyticsSummary(siteId), null),
      safeLoad(() => getSites(), [] as Site[]),
      safeLoad(() => getAlerts(), [] as Alert[]),
      safeLoad(() => getAlertLogs(), [] as AlertLog[]),
      safeLoad(() => getMqttConnections(), [] as any[]),
    ]);

    setDevices(Array.isArray(devs) ? devs : []);
    setSummary(sum);
    setSites(Array.isArray(siteList) ? siteList : []);
    setAlertRules(Array.isArray(rules) ? rules : []);
    setAlertLogs(Array.isArray(logs) ? logs : []);
    setMqttConnections(Array.isArray(mqtt) ? mqtt : []);
  }

  const filteredDevices = siteFilter
    ? devices.filter((d) => d.site_id === siteFilter)
    : devices;

  const navGroups: NavGroup[] = useMemo(
    () => [
      {
        label: 'MONITOR',
        items: [
          { id: 'overview', label: 'Overview', icon: 'gauge' },
          { id: 'dashboards', label: 'Dashboard Builder', icon: 'dashboard' },
          { id: 'live', label: 'Live Sensors', icon: 'wave' },
          { id: 'map', label: 'Floor Plan', icon: 'map' },
          { id: 'alerts', label: 'Alerts', icon: 'bell', badge: alertLogs.length },
        ],
      },
      {
        label: 'MANAGE',
        items: [
          { id: 'devices', label: 'Devices', icon: 'chip' },
          { id: 'sites', label: 'Sites & Areas', icon: 'hierarchy' },
          { id: 'onboarding', label: 'Onboarding', icon: 'checklist' },
          { id: 'mqtt', label: 'MQTT Bridges', icon: 'antenna', badge: mqttConnections.length },
        ],
      },
      {
        label: 'ANALYZE',
        items: [
          ...(canManageReports ? [{ id: 'reports' as TabId, label: 'Reports', icon: 'report' as NavIconKey }] : []),
          { id: 'export', label: 'Data Export', icon: 'download' },
          ...(isAdmin ? [{ id: 'audit' as TabId, label: 'Audit Log', icon: 'shield' as NavIconKey }] : []),
        ],
      },
      {
        label: 'ORGANIZATION',
        items: [
          ...(isAdmin ? [{ id: 'team' as TabId, label: 'Team', icon: 'users' as NavIconKey }] : []),
          { id: 'support', label: 'Support', icon: 'headset' },
          { id: 'settings', label: 'Settings', icon: 'gear' },
        ],
      },
    ],
    [alertLogs.length, canManageReports, isAdmin, mqttConnections.length]
  );

  const selectedSite = sites.find((site) => site.id === siteFilter);
  const selectedSiteLabel = selectedSite?.name || 'All Sites';
  const visibleCopy = pageCopy[activeTab];
  const totalDevices = filteredDevices.length;
  const onlineDevices = filteredDevices.filter((d) => d.status === 'online').length;
  const offlineDevices = Math.max(totalDevices - onlineDevices, 0);
  const fleetUptime = totalDevices > 0 ? (onlineDevices / totalDevices) * 100 : 0;
  const readings24h = Number(summary?.readings24h ?? 0);
  const ingestRate = readings24h / 1440;
  const enabledAlertRules = alertRules.filter((rule) => rule.enabled).length;
  const workspaceOptions = myTenants?.tenants || [];
  const showWorkspaceSwitcher = workspaceOptions.length > 1;
  const isImpersonating = !!sessionStorage.getItem('impersonator_user');
  const hasNoEnabledFeatures = !planLoading && !!tenantPlan && allowedTabIds.length === 0;
  const canUseDashboardBuilder = !isMobile && allowedTabIds.includes('dashboards');
  const canShowOverviewDashboards = planAllowedTabIds.includes('overview');
  const requiresVerification = user.role !== 'super_admin' && !verifiedAt && (
    user.signup_source === 'self_signup' || sessionStorage.getItem(`pending_verification.${user.id}`) === 'true'
  );
  const trialState = getTrialState(tenantPlan, trialStatus);
  const showPlanBanner = shouldShowPlanBanner(tenantPlan, trialStatus, trialState);
  const whiteLabelActive = isWhiteLabelActive(tenantPlan, trialStatus, trialState);
  const hasCustomBranding = tenantBranding.branding_enabled !== false && (
    !!tenantBranding.logo_url ||
    !!tenantBranding.platform_name?.trim() ||
    !!tenantBranding.platform_short_name?.trim()
  );
  const brandName = whiteLabelActive && hasCustomBranding
    ? tenantBranding.platform_name?.trim() || tenantPlan?.tenant_name || 'Seedronics'
    : 'Seedronics';
  const brandSubtitle = whiteLabelActive && hasCustomBranding
    ? tenantBranding.platform_short_name?.trim() || 'Cloud IoT'
    : 'Cloud IoT';
  const brandLogoUrl = whiteLabelActive && hasCustomBranding ? getAssetUrl(tenantBranding.logo_url || null) : null;

  async function handleSwitchTenant(tenantId: string) {
    if (!tenantId || tenantId === myTenants?.current_tenant_id) return;
    setSwitchingTenant(true);
    try {
      const data = await switchTenant(tenantId);
      localStorage.setItem('user', JSON.stringify(data.user));
      window.location.reload();
    } catch (err: any) {
      console.error('Failed to switch tenant:', err.response?.data?.error || err.message);
      setSwitchingTenant(false);
    }
  }

  function selectTab(tab: TabId) {
    setActiveTab(tab);
    if (isMobile) setMobileMenuOpen(false);
  }

  async function handleResendVerification() {
    setResendingVerification(true);
    setVerificationNotice('');
    try {
      const data = await resendVerification(user.email);
      setVerificationNotice(data.message || 'If your account is still unverified, a new verification email is on its way.');
      if (data.verification_url) {
        setVerificationUrl(data.verification_url);
        sessionStorage.setItem(`verification_url.${user.id}`, data.verification_url);
      }
    } catch (err: any) {
      setVerificationNotice(err.response?.data?.error || 'Unable to resend verification email right now.');
    } finally {
      setResendingVerification(false);
    }
  }

  async function handleRefreshVerification() {
    setCheckingVerification(true);
    setVerificationNotice('');
    try {
      const currentUser = await getCurrentUser();
      localStorage.setItem('user', JSON.stringify(currentUser));
      setVerifiedAt(currentUser.email_verified_at || null);
      if (currentUser.email_verified_at) {
        sessionStorage.removeItem(`pending_verification.${user.id}`);
        sessionStorage.removeItem(`verification_url.${user.id}`);
        setVerificationUrl('');
        setVerificationNotice('Email verified. Your workspace access is up to date.');
      } else {
        setVerificationNotice('Your email is still pending verification.');
      }
    } catch (err: any) {
      setVerificationNotice(err.response?.data?.error || 'Unable to refresh verification status right now.');
    } finally {
      setCheckingVerification(false);
    }
  }

  async function loadTenantBranding() {
    try {
      const settings = await getTenantSettings();
      setTenantBranding({
        logo_url: settings.logo_url || null,
        platform_name: settings.platform_name || null,
        platform_short_name: settings.platform_short_name || null,
        platform_description: settings.platform_description || null,
        branding_enabled: typeof settings.branding_enabled === 'boolean' ? settings.branding_enabled : null,
      });
    } catch (err: any) {
      console.error('Failed to load tenant branding:', err.response?.data?.error || err.message);
    }
  }

  useEffect(() => {
    loadTenantBranding();

    function handleBrandingUpdated(event: Event) {
      const detail = (event as CustomEvent<TenantBranding>).detail;
      if (detail) {
        setTenantBranding((current) => ({
          ...current,
          ...detail,
        }));
      } else {
        loadTenantBranding();
      }
    }

    window.addEventListener('tenant:branding-updated', handleBrandingUpdated);
    return () => window.removeEventListener('tenant:branding-updated', handleBrandingUpdated);
  }, []);

  return (
    <div className="sd-shell">
      <aside className={`sd-sidebar ${mobileMenuOpen ? 'is-open' : ''}`}>
        <div className="sd-brand">
          <div className={`sd-brand-mark ${brandLogoUrl ? 'has-logo' : ''}`}>
            {brandLogoUrl ? <img src={brandLogoUrl} alt={`${brandName} logo`} /> : getInitials(brandName)}
          </div>
          <div>
            <div className="sd-brand-name">{brandName}</div>
            <div className="sd-brand-subtitle">{brandSubtitle}</div>
          </div>
        </div>

        <nav className="sd-nav" aria-label="Primary navigation">
          {navGroups.map((group) => (
            <div className="sd-nav-group" key={group.label}>
              <div className="sd-nav-label">{group.label}</div>
              {group.items
                .filter((item) => allowedTabIds.includes(item.id))
                .map((item) => (
                  <button
                    key={item.id}
                    className={`sd-nav-item ${activeTab === item.id ? 'is-active' : ''}`}
                    onClick={() => selectTab(item.id)}
                    type="button"
                  >
                    <span className="sd-nav-icon" aria-hidden="true">
                      <NavIcon icon={item.icon} />
                    </span>
                    <span>{item.label}</span>
                    {!!item.badge && <span className="sd-nav-badge">{item.badge}</span>}
                  </button>
                ))}
            </div>
          ))}
        </nav>

        {showWorkspaceSwitcher && (
          <label className="sd-sidebar-switcher">
            <span>Workspace</span>
            <select
              aria-label="Switch workspace"
              disabled={switchingTenant}
              onChange={(event) => handleSwitchTenant(event.target.value)}
              value={myTenants?.current_tenant_id || ''}
            >
              {workspaceOptions.map((tenant) => (
                <option disabled={!tenant.is_active} key={tenant.tenant_id} value={tenant.tenant_id}>
                  {tenant.tenant_name} ({tenant.role}){!tenant.is_active ? ' - Suspended' : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="sd-sidebar-footer">
          <div className="sd-user-card">
            <div className="sd-user-avatar">{getInitials(user.email)}</div>
            <div className="sd-user-copy">
              <div className="sd-user-name">{user.email.split('@')[0]}</div>
              <div className="sd-user-role">{user.role}</div>
            </div>
          </div>
          <button className="sd-sign-out" onClick={onLogout} type="button">
            Sign Out
          </button>
        </div>
      </aside>

      {isMobile && mobileMenuOpen && (
        <button
          className="sd-mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close menu"
          type="button"
        />
      )}

      <div className="sd-workspace">
        <AlertToast />

        <header className="sd-topbar">
          {isMobile && (
            <button
              className="sd-menu-button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
              type="button"
            >
              <span />
              <span />
              <span />
            </button>
          )}

          <div className="sd-breadcrumb" aria-label="Current workspace">
            <span>{brandName}</span>
            <span className="sd-breadcrumb-divider">/</span>
            <span>{selectedSiteLabel}</span>
          </div>

          <div className="sd-topbar-actions">
            {showWorkspaceSwitcher && (
              <label className="sd-workspace-switcher">
                <span>Workspace</span>
                <select
                  aria-label="Switch workspace"
                  disabled={switchingTenant}
                  onChange={(event) => handleSwitchTenant(event.target.value)}
                  value={myTenants?.current_tenant_id || ''}
                >
                  {workspaceOptions.map((tenant) => (
                    <option disabled={!tenant.is_active} key={tenant.tenant_id} value={tenant.tenant_id}>
                      {tenant.tenant_name} ({tenant.role}){!tenant.is_active ? ' - Suspended' : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="sd-search" aria-label="Search">
              <span aria-hidden="true">Search</span>
              <input placeholder="devices, sites, alerts..." />
            </label>
            <button className="sd-secondary-button sd-help-button" onClick={() => setHelpOpen(true)} type="button">
              Help
            </button>
            <div className={`sd-live-pill ${wsConnected ? 'is-live' : 'is-stale'}`}>
              <span />
              {wsConnected ? 'WS LIVE' : 'WS STALE'}
            </div>
            <div className="sd-plan-pill">
              <span>{planLoading ? 'Loading plan' : tenantPlan?.plan_name || 'Plan unavailable'}</span>
              <small>Plan</small>
            </div>
            <div className="sd-role-pill">{user.role}</div>
          </div>
        </header>

        {isImpersonating && (
          <div className="sd-impersonation-banner">
            <span>You are viewing this tenant as a super admin.</span>
            <button onClick={onEndImpersonation} type="button">Return to Super Admin</button>
          </div>
        )}

        {isMobile && (
          <WorkspaceStatusStrip
            checkingVerification={checkingVerification}
            onRefreshVerification={handleRefreshVerification}
            onResendVerification={handleResendVerification}
            onUpgradePlan={() => setShowUpgradeModal(true)}
            planLoading={planLoading}
            resendingVerification={resendingVerification}
            requiresVerification={requiresVerification}
            showPlanActions={showPlanBanner}
            tenantPlan={tenantPlan}
            trialLoading={trialLoading}
            trialState={trialState}
            userEmail={user.email}
            verificationNotice={verificationNotice}
            verificationUrl={verificationUrl}
            wsConnected={wsConnected}
          />
        )}

        {requiresVerification && !isMobile && (
          <VerificationBanner
            checking={checkingVerification}
            notice={verificationNotice}
            onRefresh={handleRefreshVerification}
            onResend={handleResendVerification}
            resending={resendingVerification}
            verificationUrl={verificationUrl}
            userEmail={user.email}
          />
        )}

        {showPlanBanner && !isMobile && (
          <TrialPlanBanner
            onUpgrade={() => setShowUpgradeModal(true)}
            planLoading={planLoading}
            tenantPlan={tenantPlan}
            trialLoading={trialLoading}
            trialState={trialState}
          />
        )}

        <main className="sd-main">
          {hasNoEnabledFeatures ? (
            <section className="sd-page-stack">
              <article className="sd-panel sd-no-features-card">
                <div className="sd-panel-header">
                  <div>
                    <h2>No features available</h2>
                    <p>{tenantPlan.plan_name} does not currently expose any tenant panels.</p>
                  </div>
                </div>
                <p>
                  Ask a super admin to update this tenant's plan or assign a plan with dashboard features enabled.
                </p>
              </article>
            </section>
          ) : (
            <>
              {activeTab === 'overview' && (
                <OverviewPage
                  alertLogs={alertLogs}
                  devices={filteredDevices}
                  enabledAlertRules={enabledAlertRules}
                  fleetUptime={fleetUptime}
                  ingestRate={ingestRate}
                  mqttConnections={mqttConnections}
                  offlineDevices={offlineDevices}
                  onlineDevices={onlineDevices}
                  readings24h={readings24h}
                  selectTab={selectTab}
                  setHours={setHours}
                  setSiteFilter={setSiteFilter}
                  showDefaultDashboard={canShowOverviewDashboards}
                  siteFilter={siteFilter}
                  sites={sites}
                  tenantPlan={tenantPlan}
                  totalDevices={totalDevices}
                  user={user}
                  hours={hours}
                />
              )}

              {activeTab === 'live' && (
                <section className="sd-page-stack">
                  <PageHeading copy={visibleCopy} />
                  <DashboardFilters
                    hours={hours}
                    setHours={setHours}
                    setSiteFilter={setSiteFilter}
                    siteFilter={siteFilter}
                    sites={sites}
                  />
                  <LiveReadings siteId={siteFilter || undefined} />
                </section>
              )}

              {activeTab === 'dashboards' && canUseDashboardBuilder && (
                <section className="sd-page-stack">
                  <PageHeading copy={visibleCopy} />
                  <DashboardsPanel tenantPlan={tenantPlan} user={user} />
                </section>
              )}

              {activeTab === 'devices' && allowedTabIds.includes('devices') && <DevicesPage />}
              {activeTab === 'sites' && allowedTabIds.includes('sites') && <SitesPage user={user} />}
              {activeTab === 'map' && allowedTabIds.includes('map') && <MapPage />}
              {activeTab === 'alerts' && allowedTabIds.includes('alerts') && <AlertsPage />}
              {activeTab === 'settings' && allowedTabIds.includes('settings') && <SettingsPage />}
              {activeTab === 'mqtt' && allowedTabIds.includes('mqtt') && <MqttConnectionsPage />}
              {activeTab === 'onboarding' && allowedTabIds.includes('onboarding') && <OnboardingWizard />}
              {activeTab === 'export' && allowedTabIds.includes('export') && <DataExportPage devices={devices} />}
              {activeTab === 'team' && isAdmin && allowedTabIds.includes('team') && <TeamPage user={user} />}
              {activeTab === 'audit' && isAdmin && allowedTabIds.includes('audit') && <AuditPage />}
              {activeTab === 'support' && allowedTabIds.includes('support') && <SupportPage />}
              {activeTab === 'reports' && canManageReports && allowedTabIds.includes('reports') && <ReportsPage />}
            </>
          )}
        </main>

        {isMobile && !['alerts', 'export', 'onboarding', 'settings', 'support'].includes(activeTab) && (
          <nav className="sd-mobile-tabs" aria-label="Quick navigation">
            {[
              { id: 'overview' as TabId, label: 'Overview' },
              { id: 'map' as TabId, label: 'Map' },
              { id: 'devices' as TabId, label: 'Devices' },
              { id: 'alerts' as TabId, label: 'Alerts', badge: alertLogs.length },
            ].filter((item) => allowedTabIds.includes(item.id)).map((item) => (
              <button
                key={item.id}
                className={activeTab === item.id ? 'is-active' : ''}
                onClick={() => selectTab(item.id)}
                type="button"
              >
                <span>{item.label}</span>
                {!!item.badge && <strong>{item.badge}</strong>}
              </button>
            ))}
          </nav>
        )}
      </div>

      {showUpgradeModal && (
        <UpgradePlanModal
          currentPlan={tenantPlan}
          canRequestUpgrade={isAdmin}
          onClose={() => setShowUpgradeModal(false)}
          plans={publicPlans}
          plansLoading={publicPlansLoading}
          trialState={trialState}
        />
      )}

      <HelpDrawer
        activeTab={activeTab}
        activeTabLabel={visibleCopy.title}
        onClose={() => setHelpOpen(false)}
        onOpenSupport={() => {
          setHelpOpen(false);
          selectTab('support');
        }}
        open={helpOpen}
      />
    </div>
  );
}

type TrialState = {
  status: 'active' | 'ended' | 'not_trial' | 'unknown';
  label: string;
  detail: string;
  daysRemaining?: number;
};

const fallbackUpgradePlans: PlanPublic[] = [
  {
    id: 'trial',
    name: 'Trial',
    slug: 'trial',
    features: ['overview', 'dashboards', 'alerts', 'map'],
    is_default: false,
    max_devices: 3,
    max_users: 1,
    max_dashboards: 1,
    price_monthly: 0,
    currency: 'USD',
  },
  {
    id: 'growth',
    name: 'Growth',
    slug: 'growth',
    features: ['overview', 'dashboards', 'devices', 'sites', 'reports', 'team'],
    is_default: false,
    max_devices: 10,
    max_users: 3,
    max_dashboards: 5,
    price_monthly: 49,
    currency: 'USD',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    slug: 'enterprise',
    features: ['overview', 'dashboards', 'devices', 'sites', 'reports', 'team', 'audit', 'mqtt'],
    is_default: false,
    max_devices: 100,
    max_users: 5,
    max_dashboards: 50,
    price_monthly: 199,
    currency: 'USD',
  },
  {
    id: 'custom',
    name: 'Custom Solution',
    slug: 'custom',
    features: ['overview', 'dashboards', 'devices', 'sites', 'reports', 'team', 'audit', 'mqtt'],
    is_default: false,
    max_devices: null,
    max_users: null,
    max_dashboards: null,
    price_monthly: null,
    currency: 'USD',
  },
];

const upgradePlanOrder = ['trial', 'free', 'growth', 'enterprise', 'custom'];
const upgradeFeatureLabels: Record<string, string> = {
  overview: 'Operational overview and live sensors',
  dashboards: 'Dashboard Builder',
  map: 'Map and floor-plan monitoring',
  alerts: 'Alerts',
  devices: 'Device management',
  sites: 'Sites and areas',
  mqtt: 'MQTT bridges',
  onboarding: 'Device onboarding',
  export: 'Data export',
  reports: 'Scheduled reports',
  team: 'Team access',
  audit: 'Audit log',
  settings: 'Workspace settings',
};

const curatedUpgradePlanCopy: Record<string, { description: string; features: string[]; priceNote?: string }> = {
  trial: {
    description: 'For first sites proving live telemetry with guided onboarding.',
    features: [
      '30-day workspace trial',
      'Connect MQTT devices',
      'Live dashboards and maps',
      'Email verification and invites',
      'Guided onboarding support',
    ],
  },
  free: {
    description: 'For first sites proving live telemetry with guided onboarding.',
    features: [
      'Free starting workspace',
      'Connect MQTT devices',
      'Live dashboards and maps',
      'Email verification and invites',
      'Guided onboarding support',
    ],
  },
  growth: {
    description: 'For small and medium monitoring teams that need dependable site visibility.',
    features: [
      'Multi-site device monitoring',
      'White-labelling',
      'Dashboard Builder up to 5 dashboards',
      '10 device integrations',
      '3 users max',
      '$10 per additional device/month',
      'Scheduled reports',
      'Role-based team access',
      'Priority implementation support',
    ],
  },
  enterprise: {
    description: 'For larger deployments that need more controls, support, and reporting scope.',
    features: [
      'Everything in Growth plan',
      '50 dashboards and 100 devices',
      '5 users max',
      'Dedicated deployment support',
      'Advanced audit and retention',
      'Private infrastructure options',
      'Custom integrations and SLAs',
      'Solutions engineer on call',
    ],
  },
  custom: {
    description: 'For on-premise, private cloud, custom integrations, or dedicated SLA needs.',
    features: [
      'On-premise or private cloud hosting',
      'Custom integrations with existing systems',
      'Advanced security and access controls',
      'Dedicated deployment and training support',
      'Custom retention, reporting, and SLA terms',
      'Multi-location rollout planning',
    ],
    priceNote: 'tailored plan',
  },
};

function getVisibleUpgradePlans(plans: PlanPublic[], currentPlan: TenantPlan | null, trialState: TrialState) {
  const publicBySlug = new Map(plans.map((plan) => [plan.slug, plan]));
  const currentPaidPlan = trialState.status === 'not_trial' ? getTenantEffectivePlanSlug(currentPlan).toLowerCase() : '';
  const hideStarterPlan =
    trialState.status === 'active' ||
    trialState.status === 'ended' ||
    currentPaidPlan === 'free' ||
    currentPaidPlan === 'trial';

  return fallbackUpgradePlans
    .map((fallback) => {
      const publicPlan = publicBySlug.get(fallback.slug) || (fallback.slug === 'trial' ? publicBySlug.get('free') : undefined);
      const publicPrice = getPlanPriceNumber(publicPlan?.price_monthly);
      const fallbackPrice = getPlanPriceNumber(fallback.price_monthly);
      return {
        ...fallback,
        id: publicPlan?.id || fallback.id,
        slug: publicPlan?.slug || fallback.slug,
        name: fallback.name,
        price_monthly: publicPrice ?? fallbackPrice,
        currency: publicPlan?.currency || fallback.currency,
      };
    })
    .filter((plan) => {
      if (hideStarterPlan && (plan.slug === 'trial' || plan.slug === 'free')) return false;
      return !currentPaidPlan || !isSamePlanSlug(plan.slug, currentPaidPlan);
    })
    .sort((a, b) => getUpgradePlanRank(a.slug) - getUpgradePlanRank(b.slug));
}

function getUpgradePlanRank(planSlug: string) {
  const index = upgradePlanOrder.indexOf(planSlug);
  return index === -1 ? upgradePlanOrder.length - 1 : index;
}

function isSamePlanSlug(left: string, right: string) {
  if (left === right) return true;
  return (left === 'trial' && right === 'free') || (left === 'free' && right === 'trial');
}

function getTenantEffectivePlanSlug(tenantPlan: TenantPlan | null, trialStatus?: TenantTrialStatus | null) {
  return (
    trialStatus?.effective_plan ||
    trialStatus?.current_plan ||
    tenantPlan?.effective_plan ||
    tenantPlan?.plan ||
    ''
  );
}

function getTenantBasePlanSlug(tenantPlan: TenantPlan | null, trialStatus?: TenantTrialStatus | null) {
  return trialStatus?.base_plan || tenantPlan?.base_plan || tenantPlan?.plan || '';
}

function getTrialState(tenantPlan: TenantPlan | null, trialStatus: TenantTrialStatus | null): TrialState {
  const effectivePlanSlug = getTenantEffectivePlanSlug(tenantPlan, trialStatus);
  const basePlanSlug = getTenantBasePlanSlug(tenantPlan, trialStatus);
  const planLabel = tenantPlan?.plan_name || formatPlanTitle(effectivePlanSlug || 'plan');

  if (!tenantPlan && !trialStatus) {
    return {
      status: 'unknown',
      label: 'Plan loading',
      detail: 'Checking your workspace plan and trial status.',
    };
  }

  if (trialStatus) {
    if (trialStatus.is_trial) {
      const trialPlanLabel = formatPlanTitle(effectivePlanSlug || trialStatus.trial_plan_slug || 'trial plan');
      const basePlanLabel = formatPlanTitle(basePlanSlug || 'free');
      return {
        status: 'active',
        label: `${trialStatus.days_remaining} day${trialStatus.days_remaining === 1 ? '' : 's'} left in trial`,
        detail: trialStatus.trial_ends_at
          ? `${trialPlanLabel} trial ends on ${new Date(trialStatus.trial_ends_at).toLocaleDateString()}. ${basePlanLabel} resumes afterward unless the tenant upgrades.`
          : `You are currently evaluating the ${trialPlanLabel} plan. ${basePlanLabel} remains the fallback plan.`,
        daysRemaining: trialStatus.days_remaining,
      };
    }

    if (trialStatus.trial_ends_at && trialStatus.days_remaining <= 0) {
      return {
        status: 'ended',
        label: 'Trial ended',
        detail: `Trial ended on ${new Date(trialStatus.trial_ends_at).toLocaleDateString()}. ${formatPlanTitle(basePlanSlug || effectivePlanSlug || 'free')} is now active.`,
        daysRemaining: 0,
      };
    }

    if ((effectivePlanSlug || '').toLowerCase() === 'free') {
      return {
        status: 'not_trial',
        label: 'Free plan',
        detail: 'This workspace is on the free starting plan. Upgrade when you are ready to add more sites, dashboards, users, or devices.',
      };
    }

    return {
      status: 'not_trial',
      label: `${planLabel} plan`,
      detail: 'This workspace is not currently in an active trial. You can still request a plan change when your monitoring scope grows.',
    };
  }

  if (!tenantPlan) {
    return {
      status: 'unknown',
      label: 'Plan loading',
      detail: 'Checking your workspace plan and trial status.',
    };
  }

  if (!tenantPlan.trial_ends_at) {
    return {
      status: 'not_trial',
      label: `${tenantPlan.plan_name} plan`,
      detail: 'No active trial countdown is attached to this workspace.',
    };
  }

  const endsAt = new Date(tenantPlan.trial_ends_at);
  if (Number.isNaN(endsAt.getTime())) {
    return {
      status: 'unknown',
      label: `${tenantPlan.plan_name} plan`,
      detail: 'Trial status is unavailable for this workspace.',
    };
  }

  const diffMs = endsAt.getTime() - Date.now();
  const daysRemaining = Math.ceil(diffMs / (24 * 60 * 60 * 1000));

  if (daysRemaining <= 0) {
    return {
      status: 'ended',
      label: 'Trial ended',
      detail: `Trial ended on ${endsAt.toLocaleDateString()}. Choose a plan to keep the workspace fully active.`,
      daysRemaining: 0,
    };
  }

  return {
    status: 'active',
    label: `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left in trial`,
    detail: `${formatPlanTitle(effectivePlanSlug || tenantPlan.plan)} trial ends on ${endsAt.toLocaleDateString()}. ${formatPlanTitle(basePlanSlug || 'free')} resumes afterward unless the tenant upgrades.`,
    daysRemaining,
  };
}

function shouldShowPlanBanner(
  tenantPlan: TenantPlan | null,
  trialStatus: TenantTrialStatus | null,
  trialState: TrialState
) {
  if (trialState.status === 'unknown') return true;
  if (trialState.status === 'active' || trialState.status === 'ended') return true;
  const planSlug = getTenantEffectivePlanSlug(tenantPlan, trialStatus).toLowerCase();
  return planSlug === 'free' || planSlug === 'trial';
}

type WorkspaceStatusTone = 'healthy' | 'info' | 'warning' | 'attention' | 'neutral';

type WorkspaceStatusSummary = {
  detail: string;
  label: string;
  tone: WorkspaceStatusTone;
};

function WorkspaceStatusStrip({
  checkingVerification,
  onRefreshVerification,
  onResendVerification,
  onUpgradePlan,
  planLoading,
  resendingVerification,
  requiresVerification,
  showPlanActions,
  tenantPlan,
  trialLoading,
  trialState,
  userEmail,
  verificationNotice,
  verificationUrl,
  wsConnected,
}: {
  checkingVerification: boolean;
  onRefreshVerification: () => void;
  onResendVerification: () => void;
  onUpgradePlan: () => void;
  planLoading: boolean;
  resendingVerification: boolean;
  requiresVerification: boolean;
  showPlanActions: boolean;
  tenantPlan: TenantPlan | null;
  trialLoading: boolean;
  trialState: TrialState;
  userEmail: string;
  verificationNotice: string;
  verificationUrl: string;
  wsConnected: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const connectionSummary: WorkspaceStatusSummary = wsConnected
    ? { label: 'Live', detail: 'Online', tone: 'healthy' }
    : { label: 'Stale', detail: 'Retrying', tone: 'warning' };
  const planSummary = getWorkspacePlanSummary(tenantPlan, trialState, planLoading, trialLoading);
  const accountSummary: WorkspaceStatusSummary = requiresVerification
    ? { label: 'Verify email', detail: 'Required', tone: 'attention' }
    : { label: 'Verified', detail: 'Ready', tone: 'healthy' };
  const hasActions = requiresVerification || showPlanActions;
  const stripTone = requiresVerification || trialState.status === 'ended'
    ? 'attention'
    : !wsConnected || trialState.status === 'unknown'
      ? 'warning'
      : 'healthy';

  return (
    <section className={`sd-workspace-status-strip is-${stripTone}`} aria-label="Workspace status">
      <div className="sd-workspace-status-title">Workspace status</div>
      <div className="sd-workspace-status-row">
        <WorkspaceStatusCell icon="wave" summary={connectionSummary} />
        <WorkspaceStatusCell icon="dashboard" summary={planSummary} />
        <WorkspaceStatusCell icon="shield" summary={accountSummary} />
        {hasActions ? (
          <button
            className="sd-workspace-status-expand"
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse workspace status details' : 'Expand workspace status details'}
            onClick={() => setExpanded((value) => !value)}
            type="button"
          >
            <span>{expanded ? 'Hide' : 'Details'}</span>
            <i aria-hidden="true" />
          </button>
        ) : (
          <span className="sd-workspace-status-ready">OK</span>
        )}
      </div>

      {expanded && hasActions && (
        <div className="sd-workspace-status-details">
          {requiresVerification && (
            <div className="sd-workspace-status-detail is-attention">
              <div>
                <strong>Verify email</strong>
                <span className="sd-workspace-status-email">{userEmail}</span>
                <span>Keep full workspace access active.</span>
                {verificationNotice && <em>{verificationNotice}</em>}
                {verificationUrl && <a href={verificationUrl}>Open verification link</a>}
              </div>
              <div className="sd-workspace-status-actions">
                <button disabled={resendingVerification} onClick={onResendVerification} type="button">
                  {resendingVerification ? 'Sending...' : 'Resend'}
                </button>
                <button disabled={checkingVerification} onClick={onRefreshVerification} type="button">
                  {checkingVerification ? 'Checking...' : 'Refresh'}
                </button>
              </div>
            </div>
          )}

          {showPlanActions && (
            <div className={`sd-workspace-status-detail is-${planSummary.tone}`}>
              <div>
                <strong>{trialState.label}</strong>
                <span>{trialState.detail}</span>
              </div>
              <div className="sd-workspace-status-actions">
                <a href="/pricing">Pricing</a>
                <button onClick={onUpgradePlan} type="button">Upgrade</button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function WorkspaceStatusCell({ icon, summary }: { icon: NavIconKey; summary: WorkspaceStatusSummary }) {
  return (
    <div className={`sd-workspace-status-cell is-${summary.tone}`}>
      <span className="sd-status-icon" aria-hidden="true">
        <NavIcon icon={icon} />
      </span>
      <span className="sd-status-copy">
        <strong>{summary.label}</strong>
        <small>{summary.detail}</small>
      </span>
    </div>
  );
}

function getWorkspacePlanSummary(
  tenantPlan: TenantPlan | null,
  trialState: TrialState,
  planLoading: boolean,
  trialLoading: boolean
): WorkspaceStatusSummary {
  if (planLoading || trialLoading || trialState.status === 'unknown') {
    return { label: 'Plan sync', detail: 'Checking', tone: 'warning' };
  }

  const planName = getCompactPlanName(tenantPlan?.plan_name || trialState.label);

  if (trialState.status === 'active') {
    const days = typeof trialState.daysRemaining === 'number' ? `${trialState.daysRemaining}d trial` : 'Trial active';
    return { label: planName || 'Trial', detail: days, tone: 'info' };
  }

  if (trialState.status === 'ended') {
    return { label: 'Trial ended', detail: 'Choose plan', tone: 'attention' };
  }

  if (planName.toLowerCase() === 'free') {
    return { label: 'Free plan', detail: 'Starter', tone: 'neutral' };
  }

  return { label: planName || 'Plan ready', detail: 'Active plan', tone: 'info' };
}

function getCompactPlanName(value: string) {
  return value.replace(/\s+plan$/i, '').trim();
}

function VerificationBanner({
  checking,
  notice,
  onRefresh,
  onResend,
  resending,
  userEmail,
  verificationUrl,
}: {
  checking: boolean;
  notice: string;
  onRefresh: () => void;
  onResend: () => void;
  resending: boolean;
  userEmail: string;
  verificationUrl: string;
}) {
  return (
    <section className="sd-account-banner is-verification">
      <div>
        <span>Email verification required</span>
        <strong>Verify {userEmail} to keep full workspace access.</strong>
        <p>
          New self-service accounts can use Seedronics during the grace period, but login may be blocked later until
          the email is confirmed.
        </p>
        {notice && <em>{notice}</em>}
        {verificationUrl && <a href={verificationUrl}>Open verification link</a>}
      </div>
      <div>
        <button disabled={resending} onClick={onResend} type="button">
          {resending ? 'Sending...' : 'Resend email'}
        </button>
        <button disabled={checking} onClick={onRefresh} type="button">
          {checking ? 'Checking...' : 'I verified, refresh'}
        </button>
      </div>
    </section>
  );
}

function TrialPlanBanner({
  onUpgrade,
  planLoading,
  tenantPlan,
  trialLoading,
  trialState,
}: {
  onUpgrade: () => void;
  planLoading: boolean;
  tenantPlan: TenantPlan | null;
  trialLoading: boolean;
  trialState: TrialState;
}) {
  return (
    <section className={`sd-account-banner is-plan is-${trialState.status}`}>
      <div>
        <span>{planLoading || trialLoading ? 'Plan status' : tenantPlan?.tenant_name || 'Workspace plan'}</span>
        <strong>{trialState.label}</strong>
        <p>{trialState.detail}</p>
      </div>
      <div>
        <a href="/pricing">View pricing page</a>
        <button onClick={onUpgrade} type="button">Upgrade plan</button>
      </div>
    </section>
  );
}

function UpgradePlanModal({
  canRequestUpgrade,
  currentPlan,
  onClose,
  plans,
  plansLoading,
  trialState,
}: {
  canRequestUpgrade: boolean;
  currentPlan: TenantPlan | null;
  onClose: () => void;
  plans: PlanPublic[];
  plansLoading: boolean;
  trialState: TrialState;
}) {
  const [actionPlan, setActionPlan] = useState('');
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string; paymentUrl?: string } | null>(null);
  const visiblePlans = getVisibleUpgradePlans(plans, currentPlan, trialState);

  async function handleUpgradeRequest(plan: PlanPublic) {
    if (!canRequestUpgrade) {
      setNotice({ type: 'error', message: 'Only workspace admins can request plan upgrades.' });
      return;
    }
    if (trialState.status === 'not_trial' && isSamePlanSlug(plan.slug, getTenantEffectivePlanSlug(currentPlan).toLowerCase())) {
      setNotice({ type: 'success', message: `${plan.name} is already the current workspace plan.` });
      return;
    }

    setActionPlan(plan.slug);
    setNotice(null);
    try {
      const response = await requestPlanUpgrade(plan.slug);
      if (response.status === 'redirect' && response.payment_url) {
        setNotice({ type: 'success', message: 'Checkout is ready. Opening the payment page...', paymentUrl: response.payment_url });
        window.setTimeout(() => {
          window.location.assign(response.payment_url!);
        }, 650);
      } else {
        setNotice({
          type: 'success',
          message: `Upgrade request sent. Seedronics support${response.support_email ? ` (${response.support_email})` : ''} will follow up with next steps.`,
        });
      }
    } catch (err: any) {
      setNotice({ type: 'error', message: err.response?.data?.error || 'Failed to request this plan. Please try again.' });
    } finally {
      setActionPlan('');
    }
  }

  return (
    <div className="sd-upgrade-modal-overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose()} role="presentation">
      <section className="sd-upgrade-modal" role="dialog" aria-modal="true" aria-labelledby="sd-upgrade-title">
        <header>
          <div>
            <span>Plan upgrade</span>
            <h2 id="sd-upgrade-title">Choose how you want to scale Seedronics.</h2>
            <p>Public plans come from the billing backend. Requests use the configured payment gateway when available, otherwise Seedronics support receives the upgrade request.</p>
          </div>
          <button onClick={onClose} type="button">Close</button>
        </header>

        <div className={`sd-upgrade-gateway ${notice ? `is-${notice.type}` : ''}`}>
          <strong>{notice ? (notice.type === 'success' ? 'Request status' : 'Unable to request plan') : 'Current workspace'}</strong>
          <span>{notice?.message || `${currentPlan?.plan_name || 'Current plan'} · ${trialState.label}`}</span>
          {notice?.paymentUrl && <a href={notice.paymentUrl}>Open payment page</a>}
        </div>

        <div className="sd-upgrade-plan-grid">
          {plansLoading && <div className="sd-mobile-empty-card">Loading public plans...</div>}
          {!plansLoading && visiblePlans.map((plan) => {
            return (
            <article key={plan.id || plan.slug}>
              <div>
                <h3>{plan.name}</h3>
                <strong>{formatPlanPrice(plan.price_monthly, plan.currency)}</strong>
                <p>{describeUpgradePlan(plan)}</p>
              </div>
              <ul>
                {getUpgradePlanFeatures(plan).map((feature) => <li key={feature}>{feature}</li>)}
              </ul>
              <button disabled={actionPlan === plan.slug} onClick={() => handleUpgradeRequest(plan)} type="button">
                {actionPlan === plan.slug ? 'Requesting...' : plan.slug === 'custom' ? 'Request custom plan' : `Request ${plan.name}`}
              </button>
            </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function describeUpgradePlan(plan: PlanPublic) {
  if (curatedUpgradePlanCopy[plan.slug]) return curatedUpgradePlanCopy[plan.slug].description;
  if (getPlanPriceNumber(plan.price_monthly) === 0 || plan.slug === 'trial' || plan.slug === 'free') {
    return 'Evaluate Seedronics with a guided starting workspace.';
  }
  return 'A public Seedronics plan configured by the platform team.';
}

function getUpgradePlanFeatures(plan: PlanPublic) {
  if (curatedUpgradePlanCopy[plan.slug]) return curatedUpgradePlanCopy[plan.slug].features.slice(0, 8);

  const features: string[] = [];
  if (plan.max_dashboards != null) features.push(`${formatCompact(plan.max_dashboards)} dashboard${plan.max_dashboards === 1 ? '' : 's'}`);
  if (plan.max_devices != null) features.push(`${formatCompact(plan.max_devices)} device${plan.max_devices === 1 ? '' : 's'}`);
  if (plan.max_users != null) features.push(`${formatCompact(plan.max_users)} user${plan.max_users === 1 ? '' : 's'}`);
  for (const feature of plan.features || []) {
    const label = upgradeFeatureLabels[feature] || formatPlanTitle(feature);
    if (!features.includes(label)) features.push(label);
  }
  if (plan.slug === 'growth' && !features.includes('$10 per additional device/month')) {
    features.push('$10 per additional device/month');
  }
  if (plan.slug === 'custom') {
    features.push('Private hosting', 'Custom integrations', 'Deployment support');
  }
  return features.slice(0, 7);
}

function OverviewPage({
  alertLogs,
  devices,
  enabledAlertRules,
  fleetUptime,
  ingestRate,
  mqttConnections,
  offlineDevices,
  onlineDevices,
  readings24h,
  selectTab,
  setHours,
  setSiteFilter,
  showDefaultDashboard,
  siteFilter,
  sites,
  tenantPlan,
  totalDevices,
  user,
  hours,
}: {
  alertLogs: AlertLog[];
  devices: Device[];
  enabledAlertRules: number;
  fleetUptime: number;
  ingestRate: number;
  mqttConnections: any[];
  offlineDevices: number;
  onlineDevices: number;
  readings24h: number;
  selectTab: (tab: TabId) => void;
  setHours: (hours: number) => void;
  setSiteFilter: (site: string) => void;
  showDefaultDashboard: boolean;
  siteFilter: string;
  sites: Site[];
  tenantPlan: TenantPlan | null;
  totalDevices: number;
  user: User;
  hours: number;
}) {
  const copy = pageCopy.overview;
  const [overviewDashboards, setOverviewDashboards] = useState<DashboardSummary[]>([]);
  const [overviewDashboardId, setOverviewDashboardId] = useState('');
  const [overviewDashboardLoading, setOverviewDashboardLoading] = useState(false);

  useEffect(() => {
    if (!showDefaultDashboard) {
      setOverviewDashboards([]);
      setOverviewDashboardId('');
      return;
    }

    let mounted = true;
    setOverviewDashboardLoading(true);
    getDashboards()
      .then((items) => {
        if (!mounted) return;
        const list = Array.isArray(items) ? items : [];
        setOverviewDashboards(list);
        setOverviewDashboardId((current) => {
          if (current && list.some((dashboard) => dashboard.id === current)) return current;
          const fallback = list.find((dashboard) => dashboard.is_default) || list[0];
          return fallback?.id || '';
        });
      })
      .catch((err) => {
        console.error('Failed to load dashboard selector:', err.response?.data?.error || err.message);
      })
      .finally(() => {
        if (mounted) setOverviewDashboardLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [showDefaultDashboard]);

  return (
    <section className="sd-page-stack">
      <div className="sd-page-header">
        <PageHeading copy={copy} />
        <div className="sd-page-actions">
          <button className="sd-secondary-button" onClick={() => selectTab('alerts')} type="button">
            View alerts
          </button>
          <button className="sd-primary-button" onClick={() => selectTab('onboarding')} type="button">
            Onboard device
          </button>
        </div>
      </div>

      <DashboardFilters
        hours={hours}
        setHours={setHours}
        setSiteFilter={setSiteFilter}
        siteFilter={siteFilter}
        sites={sites}
      />

      <section className="sd-kpi-grid" aria-label="Fleet metrics">
        <KpiCard
          accent="success"
          label="ASSETS ONLINE"
          value={`${onlineDevices}/${Math.max(totalDevices, 1)}`}
          detail={`${offlineDevices} offline`}
          trend="Live heartbeat"
        />
        <KpiCard
          accent="blue"
          label="INGEST RATE"
          value={`${formatRate(ingestRate)}`}
          unit="msg/min"
          detail={`${formatCompact(readings24h)} readings in 24h`}
          trend={`${mqttConnections.length} MQTT bridge${mqttConnections.length === 1 ? '' : 's'}`}
        />
        <KpiCard
          accent="danger"
          label="ALERT EVENTS"
          value={alertLogs.length}
          detail={`${enabledAlertRules} active rule${enabledAlertRules === 1 ? '' : 's'}`}
          trend="Latest trigger log"
        />
        <KpiCard
          accent="warning"
          label="FLEET UPTIME"
          value={fleetUptime > 0 ? fleetUptime.toFixed(2) : '0.00'}
          unit="%"
          detail="Derived from device status"
          trend="30d rolling target"
        />
      </section>

      {showDefaultDashboard && (
        <section className="sd-overview-dashboard-section">
          <div className="sd-page-header sd-overview-dashboard-title">
            <div className="sd-heading">
              <div className="sd-eyebrow">DEFAULT VIEW</div>
              <h1>Dashboard View</h1>
              <p>Select a saved dashboard for read-only operational monitoring.</p>
            </div>
            <div className="sd-page-actions">
              <label className="sd-overview-dashboard-picker">
                <span>Dashboard</span>
                <select
                  aria-label="Select overview dashboard"
                  disabled={overviewDashboardLoading || overviewDashboards.length === 0}
                  onChange={(event) => setOverviewDashboardId(event.target.value)}
                  value={overviewDashboardId}
                >
                  {overviewDashboards.length === 0 && (
                    <option value="">{overviewDashboardLoading ? 'Loading dashboards' : 'No dashboards'}</option>
                  )}
                  {overviewDashboards.map((dashboard) => (
                    <option key={dashboard.id} value={dashboard.id}>
                      {dashboard.is_default ? 'Default - ' : ''}{dashboard.name} ({dashboard.widget_count})
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
          <DashboardsPanel
            user={user}
            readOnly
            variant="overview"
            selectedDashboardId={overviewDashboardId}
            onDashboardSelect={setOverviewDashboardId}
            tenantPlan={tenantPlan}
          />
        </section>
      )}

      <section className="sd-overview-grid sd-overview-grid-secondary">
        <SitesPanel devices={devices} selectTab={selectTab} sites={sites} />
        <RecentAlertActivity alertLogs={alertLogs} selectTab={selectTab} />
      </section>

      <DeviceFleetPreview devices={devices} selectTab={selectTab} />

      <LiveReadings siteId={siteFilter || undefined} />
    </section>
  );
}

function NavIcon({ icon }: { icon: NavIconKey }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 2,
  };

  return (
    <svg viewBox="0 0 24 24" focusable="false">
      {icon === 'gauge' && (
        <>
          <path {...common} d="M4 14a8 8 0 0 1 16 0" />
          <path {...common} d="M12 14l4-5" />
          <path {...common} d="M7 17h10" />
        </>
      )}
      {icon === 'dashboard' && (
        <>
          <rect {...common} x="4" y="4" width="7" height="7" rx="1.5" />
          <rect {...common} x="13" y="4" width="7" height="5" rx="1.5" />
          <rect {...common} x="13" y="11" width="7" height="9" rx="1.5" />
          <rect {...common} x="4" y="13" width="7" height="7" rx="1.5" />
        </>
      )}
      {icon === 'wave' && <path {...common} d="M3 12h4l2-7 4 14 2-7h6" />}
      {icon === 'map' && (
        <>
          <path {...common} d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3z" />
          <path {...common} d="M9 3v15" />
          <path {...common} d="M15 6v15" />
        </>
      )}
      {icon === 'bell' && (
        <>
          <path {...common} d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
          <path {...common} d="M10 21h4" />
        </>
      )}
      {icon === 'chip' && (
        <>
          <rect {...common} x="7" y="7" width="10" height="10" rx="2" />
          <path {...common} d="M4 9h3M4 15h3M17 9h3M17 15h3M9 4v3M15 4v3M9 17v3M15 17v3" />
        </>
      )}
      {icon === 'hierarchy' && (
        <>
          <rect {...common} x="4" y="4" width="6" height="6" rx="1" />
          <rect {...common} x="14" y="14" width="6" height="6" rx="1" />
          <rect {...common} x="4" y="14" width="6" height="6" rx="1" />
          <path {...common} d="M10 7h4v10h-4" />
        </>
      )}
      {icon === 'checklist' && (
        <>
          <path {...common} d="M9 6h11M9 12h11M9 18h11" />
          <path {...common} d="M4 6l1 1 2-2M4 12l1 1 2-2M4 18l1 1 2-2" />
        </>
      )}
      {icon === 'antenna' && (
        <>
          <path {...common} d="M12 18V9" />
          <path {...common} d="M8 13a4 4 0 0 1 8 0" />
          <path {...common} d="M5 10a8 8 0 0 1 14 0" />
          <path {...common} d="M12 18l-3 3h6l-3-3z" />
        </>
      )}
      {icon === 'report' && (
        <>
          <path {...common} d="M6 3h9l3 3v15H6z" />
          <path {...common} d="M14 3v4h4" />
          <path {...common} d="M9 16v-4M12 16V9M15 16v-2" />
        </>
      )}
      {icon === 'download' && (
        <>
          <path {...common} d="M12 3v12" />
          <path {...common} d="M8 11l4 4 4-4" />
          <path {...common} d="M5 20h14" />
        </>
      )}
      {icon === 'shield' && (
        <>
          <path {...common} d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z" />
          <path {...common} d="M9 12h6M9 16h4" />
        </>
      )}
      {icon === 'users' && (
        <>
          <path {...common} d="M16 19v-1a4 4 0 0 0-8 0v1" />
          <circle {...common} cx="12" cy="9" r="3" />
          <path {...common} d="M20 19v-1a3 3 0 0 0-3-3" />
          <path {...common} d="M17 6a3 3 0 0 1 0 6" />
        </>
      )}
      {icon === 'headset' && (
        <>
          <path {...common} d="M4 13a8 8 0 0 1 16 0" />
          <path {...common} d="M18 19c0 1.1-.9 2-2 2h-3" />
          <rect {...common} x="2.8" y="12" width="4" height="6" rx="2" />
          <rect {...common} x="17.2" y="12" width="4" height="6" rx="2" />
          <path {...common} d="M13 21h-2" />
        </>
      )}
      {icon === 'help' && (
        <>
          <circle {...common} cx="12" cy="12" r="9" />
          <path {...common} d="M9.5 9a2.7 2.7 0 0 1 5.1 1.3c0 1.9-2.6 2.1-2.6 4" />
          <path {...common} d="M12 18h.01" />
        </>
      )}
      {icon === 'gear' && (
        <>
          <circle {...common} cx="12" cy="12" r="3" />
          <path {...common} d="M19 12a7 7 0 0 0-.1-1l2-1.5-2-3.4-2.4 1a7 7 0 0 0-1.7-1L14.5 3h-5l-.3 3.1a7 7 0 0 0-1.7 1l-2.4-1-2 3.4L5.1 11a7 7 0 0 0 0 2l-2 1.5 2 3.4 2.4-1a7 7 0 0 0 1.7 1l.3 3.1h5l.3-3.1a7 7 0 0 0 1.7-1l2.4 1 2-3.4-2-1.5c.1-.3.1-.6.1-1z" />
        </>
      )}
    </svg>
  );
}

function PageHeading({ copy }: { copy: { eyebrow: string; title: string; subtitle: string } }) {
  return (
    <div className="sd-heading">
      <div className="sd-eyebrow">{copy.eyebrow}</div>
      <h1>{copy.title}</h1>
      <p>{copy.subtitle}</p>
    </div>
  );
}

function DashboardFilters({
  hours,
  setHours,
  setSiteFilter,
  siteFilter,
  sites,
}: {
  hours: number;
  setHours: (hours: number) => void;
  setSiteFilter: (site: string) => void;
  siteFilter: string;
  sites: Site[];
}) {
  return (
    <div className="sd-filter-row">
      <label>
        <span>Site</span>
        <select value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}>
          <option value="">All Sites</option>
          {sites.map((site) => (
            <option key={site.id} value={site.id}>
              {site.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>Window</span>
        <select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
          <option value={1}>Last hour</option>
          <option value={6}>Last 6 hours</option>
          <option value={24}>Last 24 hours</option>
          <option value={168}>Last 7 days</option>
        </select>
      </label>
    </div>
  );
}

function KpiCard({
  accent,
  detail,
  label,
  trend,
  unit,
  value,
}: {
  accent: 'blue' | 'danger' | 'success' | 'warning';
  detail: string;
  label: string;
  trend: string;
  unit?: string;
  value: string | number;
}) {
  return (
    <article className={`sd-kpi-card sd-kpi-${accent}`}>
      <div className="sd-kpi-label">{label}</div>
      <div className="sd-kpi-value">
        {value}
        {unit && <span>{unit}</span>}
      </div>
      <div className="sd-kpi-detail">{detail}</div>
      <div className="sd-kpi-footer">
        <span>{trend}</span>
        <MiniSparkline accent={accent} />
      </div>
    </article>
  );
}

function MiniSparkline({ accent }: { accent: 'blue' | 'danger' | 'success' | 'warning' }) {
  const color = {
    blue: '#0A84FF',
    danger: '#FF453A',
    success: '#30D158',
    warning: '#FF9F0A',
  }[accent];
  const points =
    accent === 'danger'
      ? '4,10 20,15 38,15 54,22 72,22 88,16 104,28 118,28'
      : '4,30 18,30 30,20 48,20 62,30 78,10 96,10 112,20 124,10';

  return (
    <svg className="sd-mini-spark" viewBox="0 0 128 38" aria-hidden="true">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SitesPanel({
  devices,
  selectTab,
  sites,
}: {
  devices: Device[];
  selectTab: (tab: TabId) => void;
  sites: Site[];
}) {
  return (
    <article className="sd-panel sd-sites-panel">
      <div className="sd-panel-header">
        <div>
          <h2>Sites</h2>
          <p>Hierarchy - site / area / device</p>
        </div>
      </div>
      <div className="sd-site-list">
        {sites.length === 0 && <div className="sd-empty-row">No sites configured yet.</div>}
        {sites.slice(0, 5).map((site) => {
          const siteDeviceCount = devices.filter((device) => device.site_id === site.id).length;
          return (
            <div className="sd-site-row" key={site.id}>
              <div>
                <strong>{site.name}</strong>
                <span>{site.areas.length} area{site.areas.length === 1 ? '' : 's'}</span>
              </div>
              <b>{siteDeviceCount}</b>
            </div>
          );
        })}
      </div>
      <button className="sd-link-button" onClick={() => selectTab('sites')} type="button">
        View all sites
      </button>
    </article>
  );
}

function RecentAlertActivity({
  alertLogs,
  selectTab,
}: {
  alertLogs: AlertLog[];
  selectTab: (tab: TabId) => void;
}) {
  return (
    <article className="sd-panel sd-alert-panel">
      <div className="sd-panel-header">
        <div>
          <h2>Recent Alert Activity</h2>
          <p>Latest threshold triggers from the backend alert log</p>
        </div>
        <button className="sd-link-button" onClick={() => selectTab('alerts')} type="button">
          Open alerts
        </button>
      </div>
      <div className="sd-table-wrap sd-recent-alert-table-wrap">
        <table className="sd-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Device</th>
              <th>Message</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {alertLogs.length === 0 && (
              <tr>
                <td colSpan={4}>No alert events yet.</td>
              </tr>
            )}
            {alertLogs.slice(0, 5).map((log) => (
              <tr key={log.id}>
                <td className="sd-mono">{formatTime(log.triggered_at)}</td>
                <td className="sd-mono">{log.device_name || log.device_id?.slice(0, 8) || 'Unknown'}</td>
                <td>{formatAlertMessage(log)}</td>
                <td>
                  <span className="sd-state-chip is-open">Triggered</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="sd-recent-alert-card-list sd-mobile-record-list">
        {alertLogs.length === 0 ? (
          <div className="sd-mobile-empty-card">No alert events yet.</div>
        ) : (
          alertLogs.slice(0, 5).map((log) => (
            <article className="sd-mobile-record-card" key={log.id}>
              <header className="sd-mobile-record-header">
                <span className="sd-state-chip is-open">Triggered</span>
                <time className="sd-mono">{formatTime(log.triggered_at)}</time>
              </header>
              <div className="sd-mobile-record-title">
                <strong>{log.device_name || log.device_id?.slice(0, 8) || 'Unknown'}</strong>
                <span>{formatAlertMessage(log)}</span>
              </div>
            </article>
          ))
        )}
      </div>
    </article>
  );
}

function DeviceFleetPreview({
  devices,
  selectTab,
}: {
  devices: Device[];
  selectTab: (tab: TabId) => void;
}) {
  const pageSize = 10;
  const [fleetPage, setFleetPage] = useState(0);
  const sortedDevices = useMemo(
    () => [...devices].sort((a, b) => Number(a.status === 'online') - Number(b.status === 'online')),
    [devices]
  );
  const totalPages = Math.max(1, Math.ceil(sortedDevices.length / pageSize));
  const pageStart = fleetPage * pageSize;
  const pageDevices = sortedDevices.slice(pageStart, pageStart + pageSize);

  useEffect(() => {
    setFleetPage((current) => Math.min(current, totalPages - 1));
  }, [totalPages]);

  return (
    <article className="sd-panel">
      <div className="sd-panel-header">
        <div>
          <h2>Device Fleet</h2>
          <p>{devices.length} registered asset{devices.length === 1 ? '' : 's'} in scope</p>
        </div>
        <button className="sd-link-button" onClick={() => selectTab('devices')} type="button">
          View devices
        </button>
      </div>
      <div className="sd-table-wrap sd-device-fleet-table-wrap">
        <table className="sd-table sd-device-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Device</th>
              <th>Site / Area</th>
              <th>Topic</th>
              <th>Last seen</th>
            </tr>
          </thead>
          <tbody>
            {pageDevices.length === 0 && (
              <tr>
                <td colSpan={5}>No devices registered yet.</td>
              </tr>
            )}
            {pageDevices.map((device) => (
              <tr key={device.id}>
                <td>
                  <StatusPill status={device.status} />
                </td>
                <td>
                  <strong>{device.name}</strong>
                  <span>{device.location || 'No location set'}</span>
                </td>
                <td>{[device.site_name, device.area_name].filter(Boolean).join(' / ') || 'Unassigned'}</td>
                <td className="sd-mono">{device.topic}</td>
                <td>{formatAgo(device.last_reading_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="sd-fleet-card-list">
        {pageDevices.length === 0 ? (
          <div className="sd-mobile-empty-card">No devices registered yet.</div>
        ) : (
          pageDevices.map((device) => (
            <article className="sd-fleet-card" key={device.id}>
              <div className="sd-fleet-card-header">
                <StatusPill status={device.status} />
                <span>{formatAgo(device.last_reading_at)}</span>
              </div>
              <div className="sd-fleet-card-title">
                <strong>{device.name}</strong>
                <span>{device.location || 'No location set'}</span>
              </div>
              <div className="sd-mobile-fact-grid">
                <div className="sd-mobile-fact">
                  <span>Site / Area</span>
                  <strong>{[device.site_name, device.area_name].filter(Boolean).join(' / ') || 'Unassigned'}</strong>
                </div>
                <div className="sd-mobile-fact">
                  <span>Topic</span>
                  <code>{formatMobileTopic(device.topic)}</code>
                </div>
              </div>
            </article>
          ))
        )}
      </div>
      {sortedDevices.length > pageSize && (
        <div className="sd-pagination-footer">
          <span>
            Showing {pageStart + 1}-{Math.min(pageStart + pageSize, sortedDevices.length)} of {sortedDevices.length}
          </span>
          <div className="sd-row-actions">
            <button disabled={fleetPage === 0} onClick={() => setFleetPage((page) => Math.max(0, page - 1))} type="button">
              Previous
            </button>
            <button disabled={fleetPage >= totalPages - 1} onClick={() => setFleetPage((page) => Math.min(totalPages - 1, page + 1))} type="button">
              Next
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

function formatMobileTopic(topic?: string) {
  if (!topic) return '--';
  const parts = topic.split('/').filter(Boolean);
  if (parts.length <= 3) return topic;
  return `.../${parts.slice(-3).join('/')}`;
}

function StatusPill({ status }: { status?: 'online' | 'offline' }) {
  const online = status === 'online';
  return (
    <span className={`sd-status-pill ${online ? 'is-online' : 'is-offline'}`}>
      <span />
      {online ? 'Online' : 'Offline'}
    </span>
  );
}

function formatCompact(value: number) {
  if (!Number.isFinite(value)) return '0';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}m`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return Math.round(value).toLocaleString();
}

function getPlanPriceNumber(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function formatPlanPrice(value: number | string | null | undefined, currency?: string | null) {
  const numeric = getPlanPriceNumber(value);
  if (numeric === null) return 'Custom';
  if (numeric === 0) return 'Free';
  if (!currency || currency.toUpperCase() === 'USD') {
    return `$${numeric.toLocaleString(undefined, { maximumFractionDigits: numeric % 1 === 0 ? 0 : 2 })}/mo`;
  }
  try {
    return `${new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: numeric % 1 === 0 ? 0 : 2,
    }).format(numeric)}/mo`;
  } catch {
    return `${currency || '$'} ${numeric.toLocaleString()}/mo`;
  }
}

function formatPlanTitle(value: string) {
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatRate(value: number) {
  if (!Number.isFinite(value) || value <= 0) return '0';
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  if (value >= 10) return value.toFixed(1);
  return value.toFixed(2);
}

function formatTime(value?: string) {
  if (!value) return '--:--';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '--:--';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatAgo(value?: string) {
  if (!value) return 'No readings';
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return 'Unknown';
  const seconds = Math.max(0, Math.round((Date.now() - time) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function formatAlertMessage(log: AlertLog) {
  const actual = typeof log.actual_value === 'number' ? log.actual_value.toFixed(1) : log.actual_value;
  return `${log.metric} ${actual} matched ${log.operator} ${log.threshold}`;
}

function getInitials(email: string) {
  const name = email.split('@')[0] || 'U';
  return name
    .split(/[._-]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'U';
}

function isWhiteLabelActive(
  tenantPlan: TenantPlan | null,
  trialStatus: TenantTrialStatus | null,
  trialState: TrialState
) {
  if (trialState.status === 'ended') return false;
  if (trialStatus?.is_trial && trialStatus.days_remaining > 0) return true;
  const planSlug = getTenantEffectivePlanSlug(tenantPlan, trialStatus).toLowerCase();
  return ['growth', 'enterprise', 'custom'].includes(planSlug);
}

async function safeLoad<T>(operation: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await operation();
  } catch {
    return fallback;
  }
}
